import type { ReactNode } from 'react';

import { BaseDynamicFormFieldConfig, DynamicFieldState } from './types';

export type GenericDynamicFormComponentRegistry = Record<string, DynamicFormFieldComponent>;

// oxlint-disable-next-line typescript/no-unsafe-declaration-merging -- The interface carries the accumulating registry type implemented by the class below.
export interface DynamicFormComponentRegistry<
  TRegistry extends GenericDynamicFormComponentRegistry,
  TConfigExtra extends object = object,
> {
  register<TKind extends string, TComponent extends DynamicFormFieldComponent>(
    kind: TKind,
    component: TComponent,
  ): DynamicFormComponentRegistry<TRegistry & Record<TKind, TComponent>, TConfigExtra>;
  get<TKind extends keyof TRegistry>(kind: TKind): TRegistry[TKind];
}

export type InferDynamicFormComponentRegistry<T> =
  T extends DynamicFormComponentRegistry<
    infer TRegistry,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >
    ? TRegistry
    : never;

export type InferDynamicFormComponentConfigExtra<T> =
  T extends DynamicFormComponentRegistry<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any,
    infer TConfigExtra
  >
    ? TConfigExtra
    : never;

export type AnyDynamicFormComponentRegistry =
  DynamicFormComponentRegistry<GenericDynamicFormComponentRegistry>;

export type DynamicFormFieldConfig<
  T extends AnyDynamicFormComponentRegistry,
  TRegistry extends GenericDynamicFormComponentRegistry = InferDynamicFormComponentRegistry<T>,
  TConfigExtra extends object = InferDynamicFormComponentConfigExtra<T>,
> = {
  [TKind in keyof TRegistry]: TKind extends string
    ? TRegistry[TKind] extends DynamicFormFieldComponent<infer TConfig>
      ? TConfig & WithDynamicFormFieldConfigKind<TKind> & TConfigExtra
      : never
    : never;
}[keyof TRegistry];

export type PickDynamicFormFieldConfig<
  T,
  TKind extends keyof InferDynamicFormComponentRegistry<T> & string,
> = T extends WithDynamicFormFieldConfigKind<TKind> ? T : never;

export interface WithDynamicFormFieldConfigKind<TKind extends string> {
  kind: TKind;
}

export interface DynamicFormFieldComponent<
  TConfig extends BaseDynamicFormFieldConfig = BaseDynamicFormFieldConfig,
> {
  (props: DynamicFormComponentProps<TConfig>): ReactNode;
  /** The answer as text, for exports: needs no state, so it takes the config alone. */
  resolveReadableValues?(props: { config: TConfig }): string[];
}

export interface DynamicFormComponentProps<TConfig extends BaseDynamicFormFieldConfig> {
  config: TConfig;
  state: DynamicFieldState;
  /**
   * The id of the element that names this field, set when a wrapper renders
   * it (the question's text); the field points its control at it. Unset, the
   * field names itself from the config.
   */
  labelId?: string;
}

/** How a field names its control: by the wrapper's label, else by its own config. */
export function dynamicFieldName({
  config,
  labelId,
}: Pick<DynamicFormComponentProps<BaseDynamicFormFieldConfig>, 'config' | 'labelId'>):
  | { 'aria-labelledby': string }
  | { 'aria-label': string } {
  return labelId ? { 'aria-labelledby': labelId } : { 'aria-label': config.label ?? config.name };
}

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export class DynamicFormComponentRegistry<
  TRegistry extends GenericDynamicFormComponentRegistry,
  TConfigExtra extends object,
> implements DynamicFormComponentRegistry<TRegistry, TConfigExtra>
{
  constructor(protected readonly registry = Object.create(null) as TRegistry) {}

  register<TKind extends string, TComponent extends DynamicFormFieldComponent>(
    kind: TKind,
    component: DynamicFormFieldComponent,
  ): DynamicFormComponentRegistry<TRegistry & Record<TKind, TComponent>, TConfigExtra> {
    this.registry[kind] = component as never;

    return this as never;
  }

  get<TKind extends keyof TRegistry>(kind: TKind): TRegistry[TKind] & DynamicFormFieldComponent {
    const component = this.registry[kind];

    if (!component) {
      throw new Error(`No DynamicFormFieldComponent registered for kind '${String(kind)}'!`);
    }

    return component;
  }
}
